import './LicenseTradeIn.scss';
import * as React from 'react';
import * as Core from 'components/core';
import LicensesTableRow from 'components/account/LicensesTableRow';
import CloseModal from 'app/actionCreators/CloseModal';
import AddLicenseTradeInToCart from 'app/actionCreators/AddLicenseTradeInToCart';
import TradeInLicense from 'app/actionCreators/TradeInLicense';
import { unfetchedRemoteData, RemoteData } from 'app/store/remoteData';
import { License } from 'types/license';
import { Order } from 'types/order';
import { TradeInValue } from 'types/tradeInValue';
import { getLicenseActivations } from 'utils/license';
import { MessageWithIcon } from '@/react-ui';
import { Info } from 'lucide-react';
type Props = {
  licensesBySerial: {
    [x: License['Serial']]: RemoteData<License>;
  };
  ordersByID: {
    [x: Order['ID']]: RemoteData<Order>;
  };
  licenseSerial: License['Serial'] | undefined | null;
  licenseTradeInValueBySerial: {
    [x: License['Serial']]: RemoteData<TradeInValue> | undefined | null;
  };
  tradedIn: boolean;
  tradingIn: boolean;
  tradeInError: string | undefined | null;
  viaCart: boolean;
};
type State = {
  showFinalConfirmationPrompt: boolean;
  confirmedDeactivations: Record<number, boolean>;
};
export default class LicenseTradeIn extends React.Component<Props, State> {
  state: State = {
    showFinalConfirmationPrompt: false,
    confirmedDeactivations: {},
  };
  toggleConfirmed = (licenseID: number) => {
    return () => {
      const { confirmedDeactivations } = this.state;
      const value = !confirmedDeactivations[licenseID];
      this.setState({
        confirmedDeactivations: Object.assign({}, confirmedDeactivations, {
          [licenseID]: value,
        }),
      });
    };
  };
  confirmAllDeactivations = () => {
    const activations = this.getActivations();
    const confirmedDeactivations: State['confirmedDeactivations'] = {};
    activations.forEach((activation) => {
      confirmedDeactivations[activation.ID] = true;
    });
    this.setState({
      confirmedDeactivations,
    });
  };
  getActivations = () => {
    const license = this.getLicense();
    if (license == null) {
      return [];
    }
    return getLicenseActivations(license);
  };
  getTradeInValue = () => {
    const license = this.getLicense();
    if (license == null) {
      return unfetchedRemoteData<TradeInValue>();
    }
    const { licenseTradeInValueBySerial } = this.props;
    return (
      licenseTradeInValueBySerial[license.Serial] ||
      unfetchedRemoteData<TradeInValue>()
    );
  };
  addToCart = () => {
    const license = this.getLicense();
    if (license == null) {
      return;
    }
    AddLicenseTradeInToCart(license.Serial);
  };
  getTransaction = () => {
    const license = this.getLicense();
    if (license == null) {
      return null;
    }
    if (license.TradeInOrderID === 0) {
      return null;
    }
    const { ordersByID } = this.props;
    const orderRemoteData = ordersByID[license.TradeInOrderID];
    if (orderRemoteData == null) {
      return null;
    }
    return orderRemoteData.value;
  };
  getLicense = () => {
    const { licenseSerial, licensesBySerial } = this.props;
    if (licenseSerial == null) {
      throw new Error('license trade in started without specifying a license');
    }
    const licenseRemoteData = licensesBySerial[licenseSerial];
    if (licenseRemoteData == null) {
      return null;
    }
    return licenseRemoteData.value;
  };
  showFinalConfirmationPrompt = () => {
    this.setState({
      showFinalConfirmationPrompt: true,
    });
  };
  cancelFinalConfirmation = () => {
    this.setState({
      showFinalConfirmationPrompt: false,
    });
    CloseModal();
  };
  tradeInLicense = () => {
    const license = this.getLicense();
    if (license == null) {
      return;
    }
    TradeInLicense(license.Serial);
  };
  componentDidMount() {
    this.setState({
      confirmedDeactivations: {},
    });
  }
  render() {
    const { tradingIn, tradeInError, tradedIn, viaCart } = this.props;
    const license = this.getLicense();
    if (license == null) {
      return <Core.ActivityIndicator />;
    }
    const tradeInValueRemoteData = this.getTradeInValue();
    const zeroTradeInValue =
      tradeInValueRemoteData.value != null &&
      tradeInValueRemoteData.value.AmountCentsUSD === 0;
    const activations = this.getActivations();
    const allDeactivationsConfirmed = activations.every((activation) => {
      return this.state.confirmedDeactivations[activation.ID];
    });
    const { showFinalConfirmationPrompt } = this.state;
    if (tradedIn) {
      const transaction = this.getTransaction();
      return (
        <div className="LicenseTradeIn">
          <div className="LicenseTradeIn-heading">Trade-In Complete!</div>
          {transaction && (
            <p className="LicenseTradeIn-info">
              You can view the record of this transaction{' '}
              <Core.Link to={`/account/orders/${transaction.Number}`}>
                here
              </Core.Link>
              .
            </p>
          )}
          <Core.Button onClick={CloseModal}>Close</Core.Button>
        </div>
      );
    }
    return (
      <div className="LicenseTradeIn">
        <div className="LicenseTradeIn-heading">Trade In Your License</div>
        <p className="LicenseTradeIn-info">
          Trade in a QLab license for a discount that can be applied to
          purchases of standard, non-rental, QLab 5 licenses.
        </p>
        <div className="LicenseTradeIn-details">
          <LicensesTableRow
            license={license}
            openDetailsInNewWindow
            showLabels
          />
        </div>
        <div className="LicenseTradeIn-appraisal">
          {tradeInValueRemoteData.fetched &&
          tradeInValueRemoteData.value != null ? (
            <>
              {tradeInValueRemoteData.value.AmountCentsUSD > 0 ? (
                <>
                  <div className="LicenseTradeIn-appraisal-label">
                    Trade-in Value:
                  </div>
                  <div className="LicenseTradeIn-appraisal-value">
                    <strong>{tradeInValueRemoteData.value.Amount}</strong>
                  </div>
                </>
              ) : (
                <Core.FormError>
                  This license has no trade-in value, and cannot be traded in.
                </Core.FormError>
              )}
            </>
          ) : tradeInValueRemoteData.fetched &&
            tradeInValueRemoteData.errorMessage ? (
            <Core.ErrorBox>{`Error: ${tradeInValueRemoteData.errorMessage}`}</Core.ErrorBox>
          ) : (
            <Core.ActivityIndicator />
          )}
        </div>
        {tradingIn ? (
          <Core.ActivityIndicator message="Trading in your license" />
        ) : (
          <>
            {activations.length > 0 && (
              <div className="LicenseTradeIn-confirmDeactivations">
                <p className="LicenseTradeIn-info">
                  This license has active seats.
                </p>
                <p className="LicenseTradeIn-info">
                  To trade in this license, please confirm that the following
                  seats will be deactivated:
                </p>
                <table>
                  <tbody>
                    {activations.map((activation) => (
                      <tr
                        className="LicenseTradeIn-confirmDeactivation"
                        key={activation.ID}
                        onClick={this.toggleConfirmed(activation.ID)}
                      >
                        <td>
                          <Core.Checkbox
                            checked={
                              this.state.confirmedDeactivations[
                                activation.ID
                              ] === true
                            }
                            onChange={this.toggleConfirmed(activation.ID)}
                          >
                            {null}
                          </Core.Checkbox>
                        </td>
                        <td>{activation.seat.FullDescription}</td>
                        <td>
                          <Core.MonospacedText>
                            {activation.MachineID || ''}
                          </Core.MonospacedText>
                        </td>
                        <td>{activation.MachineName || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="LicenseTradeIn-info">
                  <Core.Button textLink onClick={this.confirmAllDeactivations}>
                    Confirm All
                  </Core.Button>
                </p>
              </div>
            )}
            {tradeInError && <Core.FormError>{tradeInError}</Core.FormError>}

            {viaCart && !zeroTradeInValue && (
              <MessageWithIcon icon={<Info className="w-4 h-4" />}>
                <div className="flex flex-col gap-3 text-sm">
                  <div>
                    Trading in this license will deactivate any active seats
                    associated with it, and prevent any further use of this
                    license.
                  </div>
                  <div>
                    <strong>
                      Once you have completed the checkout process, this action
                      cannot be undone.
                    </strong>
                  </div>
                </div>
              </MessageWithIcon>
            )}

            {showFinalConfirmationPrompt && (
              <Core.FormNotice>
                <p className="LicenseTradeIn-confirmPrompt">
                  Trading in this license will deactivate any active seats
                  associated with it, and prevent any further use of this
                  license.
                </p>
                <p className="LicenseTradeIn-confirmPrompt">
                  <strong>
                    This action cannot be undone. Are you sure you want to
                    continue?
                  </strong>
                </p>
              </Core.FormNotice>
            )}

            <Core.FormGroup
              className="justify-end flex gap-3"
              errorMessage={
                !allDeactivationsConfirmed
                  ? 'All active seats must be confirmed for deactivation before trade in.'
                  : undefined
              }
            >
              {showFinalConfirmationPrompt ? (
                <Core.Button
                  color="white"
                  onClick={this.cancelFinalConfirmation}
                >
                  Cancel
                </Core.Button>
              ) : (
                <Core.Button color="white" onClick={CloseModal}>
                  Cancel
                </Core.Button>
              )}
              {viaCart ? (
                <Core.Button
                  disabled={!allDeactivationsConfirmed || zeroTradeInValue}
                  onClick={this.addToCart}
                >
                  Add Trade-In To Cart
                </Core.Button>
              ) : showFinalConfirmationPrompt ? (
                <Core.Button
                  disabled={!allDeactivationsConfirmed}
                  onClick={this.tradeInLicense}
                >
                  Confirm
                </Core.Button>
              ) : (
                <Core.Button
                  disabled={!allDeactivationsConfirmed || zeroTradeInValue}
                  onClick={this.showFinalConfirmationPrompt}
                >
                  Trade In License
                </Core.Button>
              )}
            </Core.FormGroup>
          </>
        )}
      </div>
    );
  }
}
